import React, { useRef, useEffect, useState, useCallback } from "react";
import Webcam from "react-webcam";
import useAiAssessmentStart from "../../Pages/InterviewRounds/hooks/useAiAssessmentStart";
import { useAIAssessmentContext } from "../../Context/AIAssessmentContext/AIAssessmentContext";
import useCameraObstructionDetection from "../../Pages/InterviewRounds/hooks/useCameraObstructionDetection";
import CameraBlockedModal from "../../Pages/InterviewRounds/components/modals/CameraBlockedModal";
import "./index.scss";
import { Space } from "antd";
import { HolderOutlined } from "@ant-design/icons";

const CamRecorder: React.FC = () => {
  const webcamRef = useRef<Webcam>(null);
  // Store the stream separately so we can access it even after webcam unmounts
  const capturedStreamRef = useRef<MediaStream | null>(null);
  const { handlePermissionCancel } = useAiAssessmentStart();
  const { showCamRecorder, currentStep, isRoundFinished } = useAIAssessmentContext();
  
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);

  // Stop webcam stream helper - stops both webcamRef and capturedStreamRef
  const stopWebcamStream = useCallback(() => {
    // Stop the captured stream ref first (persists even after unmount)
    if (capturedStreamRef.current) {
      try {
        capturedStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        capturedStreamRef.current = null;
      } catch (error) {
        console.warn("Error stopping captured stream:", error);
      }
    }
    
    // Also try to stop via webcamRef if still available
    try {
      if (webcamRef.current?.video?.srcObject) {
        const stream = webcamRef.current.video.srcObject as MediaStream;
        stream.getTracks().forEach(track => {
          track.stop();
        });
        webcamRef.current.video.srcObject = null;
      }
    } catch (error) {
      console.warn("Error stopping webcam stream:", error);
    }
  }, []);

  // Capture the stream when webcam is ready
  useEffect(() => {
    const captureStream = () => {
      if (webcamRef.current?.video?.srcObject) {
        capturedStreamRef.current = webcamRef.current.video.srcObject as MediaStream;
      }
    };
    
    // Check immediately and after delays for stream to initialize
    captureStream();
    const timeoutId1 = setTimeout(captureStream, 500);
    const timeoutId2 = setTimeout(captureStream, 1500);
    
    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
    };
  }, [showCamRecorder]);

  // Cleanup webcam stream when component unmounts
  useEffect(() => {
    return () => {
      stopWebcamStream();
    };
  }, [stopWebcamStream]);

  // Stop webcam when round is finished
  useEffect(() => {
    if (isRoundFinished) {
      stopWebcamStream();
    }
  }, [isRoundFinished, stopWebcamStream]);

  // Get video element from webcam ref once it's ready
  useEffect(() => {
    const checkForVideo = () => {
      if (webcamRef.current?.video) {
        setVideoElement(webcamRef.current.video);
      }
    };
    
    // Check immediately and after a short delay (for video to initialize)
    checkForVideo();
    const timeoutId = setTimeout(checkForVideo, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [showCamRecorder]);

  // Only detect obstruction when assessment is active
  const shouldDetect = showCamRecorder && currentStep >= 0 && !isRoundFinished;

  const { isObstructed, obstructionReason, resetObstruction } = useCameraObstructionDetection({
    videoElement,
    enabled: shouldDetect,
    checkIntervalMs: 2000,
    onObstructionDetected: (reason) => {
      console.log("[CamRecorder] Camera obstruction detected:", reason);
      setShowBlockedModal(true);
      // No violation logging - just prompt the user
    },
  });

  const handleAcknowledge = () => {
    setShowBlockedModal(false);
    resetObstruction();
  };

  // Stop webcam when showCamRecorder becomes false
  useEffect(() => {
    if (!showCamRecorder) {
      stopWebcamStream();
    }
  }, [showCamRecorder, stopWebcamStream]);

  if (!showCamRecorder) return null;

  const handleUserMediaError = (error: string | DOMException) => {
    console.error("CamRecorder: Camera error:", error);
    handlePermissionCancel();
  };

  return (
    <>
      <div className="cam-recorder-container">
        <Space className="cam-recorder-handle">
          <HolderOutlined style={{ fontSize: "24px", color: "#ffffff" }} />
        </Space>
        <Webcam
          ref={webcamRef}
          audio={false}
          videoConstraints={{ facingMode: "user" }}
          onUserMediaError={handleUserMediaError}
        />
        {/* Visual indicator when camera is obstructed */}
        {isObstructed && (
          <div className="cam-recorder-blocked-overlay">
            <span>Camera Blocked</span>
          </div>
        )}
      </div>

      {/* Camera blocked warning modal - no violation logging */}
      <CameraBlockedModal
        open={showBlockedModal}
        onAcknowledge={handleAcknowledge}
        reason={obstructionReason}
      />
    </>
  );
};

export default CamRecorder;
